import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { checkFileSize } from "@/lib/api";
import { UploadLimitNote } from "@/components/UploadLimitNote";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Upload, Plus, Trash2, Edit2, FileText, X,
  Building2, Download, CheckCircle2, Search, Mail, ShieldCheck, Ban,
} from "lucide-react";
import RecipientsField from "@/components/RecipientsField";
import { addEmails } from "@/lib/emailHistory";
import { useUnsavedChanges } from "@/lib/unsavedChanges";
import ProductFilterBar, { EMPTY_PRODUCT_FILTERS } from "@/components/ProductFilterBar";
import { SortableTh, useTableSort, sortRows } from "@/components/SortableTh";
import { ClientEmailAvailability } from "@/components/ClientEmailAvailability";
import { useAuth } from "@/lib/auth";
import { ProductStatus } from "@/components/ProductStatus";
import { getActiveToken } from "@/lib/sessionToken";

const TABS = [
  { key: "anagrafica", label: "Anagrafica" },
  { key: "prodotti", label: "Prodotti Associati" },
];

export default function ClientEdit() {
  const { canWrite } = useAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("anagrafica");
  const [data, setData] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const load = async () => {
    try {
      const r = await api.get(`/clients/${id}`);
      setData(r.data);
      setBaseline(JSON.stringify(r.data));
    } catch { toast.error("Cliente non trovato"); nav("/clients"); }
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    if (!data) return false;
    if (!data.name?.trim()) { toast.error("Nome cliente obbligatorio"); return false; }
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      const r = await api.put(`/clients/${id}`, payload);
      setData(r.data);
      setBaseline(JSON.stringify(r.data));
      toast.success("Cliente salvato");
      return true;
    } catch { toast.error("Errore salvataggio"); return false; }
    finally { setSaving(false); }
  };

  // Ignore volatile / immediately-saved fields when detecting unsaved changes.
  const normClient = (s) => {
    if (!s) return s;
    try {
      const o = typeof s === "string" ? JSON.parse(s) : s;
      const { updated_at, created_at, logo_file_id, ...rest } = o;
      return JSON.stringify(rest);
    } catch { return s; }
  };
  const dirty = canWrite && !!data && baseline !== null && normClient(baseline) !== normClient(data);
  useUnsavedChanges(dirty, save);

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { logoRef.current.value = ""; return; }
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post(`/clients/${id}/logo`, fd, { headers: { "Content-Type":"multipart/form-data" } });
      setData(d => ({ ...d, logo_file_id: r.data.file_id }));
      toast.success("Logo caricato");
    } catch { toast.error("Errore upload logo"); }
    finally { logoRef.current.value = ""; }
  };

  if (!data) return <div className="p-8 text-sm text-[#707070]">Caricamento...</div>;

  return (
    <div className="workflow-ux h-full flex flex-col p-4 pt-16 md:px-8 md:py-5 lg:px-12 lg:py-6 max-w-5xl" data-testid="client-edit-page">
      <button onClick={()=>nav("/clients")} className="text-sm text-[#707070] hover:text-[#1A1A1A] flex items-center mb-3 shrink-0"
              data-testid="client-back-btn">
        <ArrowLeft className="w-3 h-3 mr-2" strokeWidth={1.5}/>Torna ai clienti
      </button>
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap shrink-0">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-0.5">Cliente</div>
          <h1 className="font-serif text-2xl md:text-3xl leading-tight">{data.name || "—"}</h1>
          {data.company_name && <div className="text-xs text-[#707070] mt-0.5">{data.company_name}</div>}
        </div>
        {tab === "anagrafica" && canWrite && (
          <button onClick={save} disabled={saving} className="btn-primary flex items-center"
                  data-testid="client-save-btn">
            <Save className="w-3 h-3 mr-2" strokeWidth={1.5}/>{saving ? "Salvataggio..." : "Salva"}
          </button>
        )}
      </div>

      <div className="flex border-b border-[#E5E5E0] mb-4 shrink-0">
        {TABS.map(t => (
          <button key={t.key} onClick={()=>setTab(t.key)}
                  className={`tab-btn ${tab === t.key ? "active" : ""}`}
                  data-testid={`client-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
      {tab === "anagrafica" && (
        <div className="space-y-6 flex-1 min-h-0 overflow-auto pr-1" data-testid="client-anagrafica-tab">
          <div className="flex items-start gap-6">
            {data.logo_file_id ? (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.logo_file_id}`}
                   className="w-24 h-24 object-contain bg-[#FAFAF7] border border-[#E5E5E0] p-2" alt={data.name}/>
            ) : (
              <div className="w-24 h-24 bg-[#F4F4F0] border border-dashed border-[#C5B39B] flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#C5B39B]" strokeWidth={1.5}/>
              </div>
            )}
            <div>
              <div className="label-tiny mb-1">Logo cliente</div>
              {canWrite && <input ref={logoRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadLogo} className="hidden"
                     data-testid="client-logo-input"/>
              }
              {canWrite && <button onClick={()=>logoRef.current.click()} className="btn-ghost flex items-center"
                      data-testid="client-logo-upload-btn">
                <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>Carica logo
              </button>}
              <div className="text-xs text-[#707070] mt-2">Usato come intestazione nel CPNP</div>
              <UploadLimitNote />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
            <Field label="Nome cliente *" value={data.name}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,name:v}))}
                    testid="client-input-name"/>
            <Field label="Ragione sociale" value={data.company_name}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,company_name:v}))}
                    testid="client-input-company"/>
            <Field label="P.IVA" value={data.vat_number}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,vat_number:v}))}
                    testid="client-input-vat"/>
            <Field label="Telefono" value={data.phone}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,phone:v}))}
                    testid="client-input-phone"/>
            <Field label="Email" value={data.email} type="email"
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,email:v}))}
                    testid="client-input-email"/>
            <Field label="Sito web" value={data.website}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,website:v}))}
                    testid="client-input-website"/>
            <Field label="Indirizzo" value={data.address} className="md:col-span-2"
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,address:v}))}
                    testid="client-input-address"/>
            <Field label="CAP" value={data.cap}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,cap:v}))}
                    testid="client-input-cap"/>
            <Field label="Città" value={data.city}
                    disabled={!canWrite}
                    onChange={v=>setData(d=>({...d,city:v}))}
                    testid="client-input-city"/>
          </div>
        </div>
      )}

      {tab === "prodotti" && <ClientProducts clientId={id} clientEmail={data.email || ""} canWrite={canWrite}/>}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "", testid, disabled }) {
  return (
    <div className={className}>
      <label className="label-tiny mb-1 block">{label}</label>
      <input type={type} value={value || ""} onChange={e => onChange(e.target.value)} disabled={disabled}
              placeholder={label.replace(/\s*\*$/, "")}
              className="input-luxury text-sm w-full" data-testid={testid}/>
    </div>
  );
}

function ClientProducts({ clientId, clientEmail, canWrite }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { id, code, name }
  const [cpnpFor, setCpnpFor] = useState(null); // client-product for single CPNP modal
  const [sdsFor, setSdsFor] = useState(null);   // client-product for single SDS modal
  const [selected, setSelected] = useState(new Set()); // multi-select client-product ids
  const [bulkOpen, setBulkOpen] = useState(null); // "zip" | "email" | "sds-zip" | null
  const [brands, setBrands] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_PRODUCT_FILTERS);
  useEffect(() => {
    api.get("/brands").then(r => setBrands(r.data)).catch(() => {});
    api.get("/categories").then(r => setCats(r.data)).catch(() => {});
  }, []);
  // Real-time client-side filtering (search + brand/category/sub-category),
  // same logic validated on the Prodotti page.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const f = filters;
    const pmin = f.price_min !== "" ? parseFloat(f.price_min) : null;
    const pmax = f.price_max !== "" ? parseFloat(f.price_max) : null;
    const minPct = f.inci_min_percent !== "" ? parseFloat(f.inci_min_percent) : null;
    const inciQ = (f.inci || "").trim().toLowerCase();
    const casQ = (f.cas || "").trim().toLowerCase();
    return items.filter(cp => {
      if (f.brand_id && cp.brand_id !== f.brand_id) return false;
      if (f.category_id && cp.category_id !== f.category_id) return false;
      if (f.subcategory_id && cp.subcategory_id !== f.subcategory_id) return false;
      if (f.code) {
        const c = `${cp.code || ""} ${cp.source_product_code || ""}`.toLowerCase();
        if (!c.includes(f.code.toLowerCase())) return false;
      }
      if (f.barcode && !(cp.barcode_number || "").toLowerCase().includes(f.barcode.toLowerCase())) return false;
      if (pmin != null && !(typeof cp.price_value === "number" && cp.price_value >= pmin)) return false;
      if (pmax != null && !(typeof cp.price_value === "number" && cp.price_value <= pmax)) return false;
      if (inciQ || casQ || minPct != null) {
        const ings = cp.ingredients || [];
        const ok = ings.some(ing => {
          if (inciQ && !(ing.name || "").toLowerCase().includes(inciQ)) return false;
          if (casQ && !(ing.cas || "").toLowerCase().includes(casQ)) return false;
          if (minPct != null && !(typeof ing.range_max === "number" && ing.range_max >= minPct)) return false;
          return true;
        });
        if (!ok) return false;
      }
      if (q) {
        const hay = `${cp.code || ""} ${cp.name || ""} ${cp.source_product_code || ""} ${cp.source_product_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, search, filters]);
  const { sortField, sortDir, toggleSort } = useTableSort();
  const sorted = useMemo(() => sortRows(filtered, sortField, sortDir, {
    code: r => r.code,
    name: r => r.name,
    source: r => r.source_product_code,
    status: r => (r.inci_approved ? 1 : 0) + (r.has_sds ? 1 : 0),
  }), [filtered, sortField, sortDir]);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${clientId}/products`);
      setItems(r.data);
      setSelected(new Set());
    } catch { toast.error("Errore caricamento"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [clientId]);
  const remove = async (cp) => {
    if (!window.confirm(`Rimuovere "${cp.code} — ${cp.name}" dal cliente?`)) return;
    try {
      await api.delete(`/clients/${clientId}/products/${cp.id}`);
      toast.success("Prodotto rimosso"); load();
    } catch { toast.error("Errore rimozione"); }
  };
  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    const ids = filtered.map(i => i.id);
    const allSel = ids.length > 0 && ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSel) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };
  const allSelected = filtered.length > 0 && filtered.every(i => selected.has(i.id));
  // Bulk readiness: at least one selected item must have INCI/SDS approved.
  const selectedItems = items.filter(i => selected.has(i.id));
  const anyInciSelected = selectedItems.some(i => i.inci_approved);
  const anySdsSelected = selectedItems.some(i => i.has_sds);
  const anyDocSelected = anyInciSelected || anySdsSelected;
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3" data-testid="client-products-tab">
      <div className="flex items-center justify-between gap-4 shrink-0">
        <div className="text-xs text-[#707070]">
          <span className="text-[#1A1A1A] font-medium" data-testid="client-associated-products-count">{items.length}</span> prodotto/i associati · ogni riga eredita i dati dal prodotto sorgente
        </div>
        {canWrite && <button onClick={()=>setAddOpen(true)} className="btn-primary flex items-center shrink-0"
                data-testid="cp-add-btn">
          <Plus className="w-3 h-3 mr-2" strokeWidth={2}/>Associa Prodotto
        </button>}
      </div>
      {loading ? (
        <div className="text-sm text-[#707070]">Caricamento...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E5E0] rounded-sm">
          <div className="text-sm text-[#707070]">Nessun prodotto associato. Clicca "Associa Prodotto" per aggiungerne uno.</div>
        </div>
      ) : (
        <>
          <div className="shrink-0">
            <ProductFilterBar
              search={search} onSearchChange={setSearch}
              filters={filters} onFiltersChange={setFilters}
              brands={brands} cats={cats}
              count={filtered.length} total={items.length}
              extended
              searchPlaceholder="Cerca per codice o nome (cliente o sorgente)..." />
          </div>
          <div className="border border-[#E5E5E0] bg-white flex-1 min-h-[17rem] overflow-auto">
          <table className="w-full text-sm sticky-thead">
            <thead className="bg-[#FAFAF7] text-[10px] uppercase tracking-luxury text-[#707070]">
              <tr>
                {canWrite && <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll}
                          data-testid="cp-select-all"
                          title={allSelected ? "Deseleziona tutti" : "Seleziona tutti"} />
                </th>}
                <SortableTh field="code" label="Codice cliente" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortableTh field="name" label="Nome cliente" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <SortableTh field="source" label="Prodotto sorgente" sortField={sortField} sortDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={canWrite ? 5 : 4} className="px-4 py-10 text-center text-sm text-[#707070]" data-testid="cp-no-results">Nessun prodotto corrisponde ai filtri selezionati.</td></tr>
              ) : sorted.map(cp => {
                const hasInci = !!cp.inci_approved;
                const hasSds = !!cp.has_sds;
                return (
                <tr key={cp.id} className="border-t border-[#F0F0EA] hover:bg-[#FAFAF7]" data-testid={`cp-row-${cp.id}`}>
                  {canWrite && <td className="px-4 py-3">
                    <input type="checkbox" checked={selected.has(cp.id)} onChange={() => toggleOne(cp.id)}
                            data-testid={`cp-select-${cp.id}`}/>
                  </td>}
                  <td className="px-4 py-3 font-mono text-xs">{cp.code}</td>
                  <td className="px-4 py-3">{cp.name}</td>
                  <td className="px-4 py-3 text-[#707070] text-xs">
                    {cp.source_product_code} — {cp.source_product_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap justify-end gap-2" data-testid={`cp-availability-${cp.id}`}>
                      {hasInci && canWrite ? (
                        <ProductStatus as="button" type="button" tone="success" icon={Download}
                                       className="workflow-ux-download" onClick={()=>setCpnpFor(cp)}
                                       title="Scarica CPNP/INCI con intestazione cliente"
                                       aria-label="INCI approvato. Scarica CPNP/INCI"
                                       testId={`cp-cpnp-${cp.id}`}>
                          <span data-testid={`cp-inci-status-${cp.id}`}>INCI approvato · Scarica</span>
                        </ProductStatus>
                      ) : (
                        <ProductStatus tone={hasInci ? "neutral" : "warning"} icon={hasInci ? undefined : Ban}
                                       title={hasInci ? "Download non disponibile in sola lettura" : "INCI non approvato: nessun download"}
                                       testId={`cp-inci-status-${cp.id}`}>
                          {hasInci ? "INCI approvato · Sola lettura" : "INCI mancante · Non scaricabile"}
                        </ProductStatus>
                      )}
                      {hasSds && canWrite ? (
                        <ProductStatus as="button" type="button" tone="success" icon={Download}
                                       className="workflow-ux-download" onClick={()=>setSdsFor(cp)}
                                       title="Scarica la scheda di sicurezza del fornitore"
                                       aria-label="SDS disponibile. Scarica la scheda di sicurezza"
                                       testId={`cp-sds-${cp.id}`}>
                          <span data-testid={`cp-sds-status-${cp.id}`}>SDS disponibile · Scarica</span>
                        </ProductStatus>
                      ) : (
                        <ProductStatus tone={hasSds ? "neutral" : "warning"} icon={hasSds ? undefined : Ban}
                                       title={hasSds ? "Download non disponibile in sola lettura" : "SDS mancante: nessun download"}
                                       testId={`cp-sds-status-${cp.id}`}>
                          {hasSds ? "SDS disponibile · Sola lettura" : "SDS mancante · Non scaricabile"}
                        </ProductStatus>
                      )}
                    </div>
                    {canWrite && <div className="flex items-center justify-end gap-3 mt-2">
                    <button onClick={()=>setEditing(cp)} className="text-[#707070] hover:text-[#1A1A1A]"
                            title="Modifica codice/nome" data-testid={`cp-edit-${cp.id}`}>
                      <Edit2 className="w-3 h-3"/>
                    </button>
                    <button onClick={()=>remove(cp)} className="text-[#8C3A3A]"
                            title="Rimuovi associazione" data-testid={`cp-delete-${cp.id}`}>
                      <Trash2 className="w-3 h-3"/>
                    </button>
                    </div>}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </>
      )}
      {items.length > 0 && canWrite && (
        <div className="flex items-center justify-end flex-wrap gap-3 shrink-0" data-testid="cp-email-bar">
          <div className="flex items-center flex-wrap gap-3 mr-auto">
            <span className="text-xs text-[#707070]" data-testid="cp-selected-count">
              {selected.size > 0 ? `${selected.size} prodotto/i selezionato/i` : "Seleziona i prodotti da scaricare o inviare al cliente"}
            </span>
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())}
                      className="btn-ghost !px-3 !py-2 inline-flex items-center gap-2"
                      data-testid="cp-clear-selection-btn">
                <X className="w-3.5 h-3.5" aria-hidden="true" strokeWidth={1.8} />Pulisci selezione
              </button>
            )}
          </div>
          <button onClick={() => setBulkOpen("zip")}
                  disabled={!anyInciSelected}
                  className="btn-ghost flex items-center disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="cp-bulk-zip-btn"
                  title={anyInciSelected
                    ? "Scarica ZIP con schede CPNP/INCI dei prodotti selezionati"
                    : "Nessuno dei prodotti selezionati ha un INCI approvato"}>
            <Download className="w-3 h-3 mr-2" strokeWidth={1.5} />Scarica ZIP CPNP/INCI
          </button>
          <button onClick={() => setBulkOpen("sds-zip")}
                  disabled={!anySdsSelected}
                  className="btn-ghost flex items-center disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="cp-bulk-sds-btn"
                  title={anySdsSelected
                    ? "Scarica ZIP con le schede di sicurezza (PDF fornitore) dei prodotti selezionati"
                    : "Nessuno dei prodotti selezionati ha una scheda di sicurezza caricata"}>
            <ShieldCheck className="w-3 h-3 mr-2" strokeWidth={1.5} />Scarica ZIP SDS
          </button>
          <button onClick={() => setBulkOpen("email")}
                  disabled={!anyDocSelected}
                  className="btn-primary flex items-center disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="cp-bulk-email-btn"
                  title={anyDocSelected
                    ? "Invia via email i documenti (CPNP/INCI · SDS) dei prodotti selezionati al cliente"
                    : "Seleziona almeno un prodotto con INCI o SDS disponibile"}>
            <Mail className="w-3 h-3 mr-2" strokeWidth={1.5} />Invia via email
          </button>
        </div>
      )}
      {addOpen && (
        <AddClientProductModal
          clientId={clientId}
          existingCodes={items.map(i => i.code)}
          onClose={()=>setAddOpen(false)}
          onSaved={() => { setAddOpen(false); load(); }}
        />
      )}
      {editing && (
        <EditClientProductModal
          clientId={clientId}
          item={editing}
          existingCodes={items.filter(i => i.id !== editing.id).map(i => i.code)}
          onClose={()=>setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {cpnpFor && (
        <CpnpLangModal
          clientId={clientId}
          cp={cpnpFor}
          onClose={()=>setCpnpFor(null)}
        />
      )}
      {sdsFor && (
        <SdsLangModal
          clientId={clientId}
          cp={sdsFor}
          onClose={()=>setSdsFor(null)}
        />
      )}
      {bulkOpen === "sds-zip" && (
        <BulkSdsModal
          clientId={clientId}
          clientProductIds={Array.from(selected)}
          items={items.filter(i => selected.has(i.id))}
          onClose={()=>setBulkOpen(null)}
        />
      )}
      {(bulkOpen === "zip" || bulkOpen === "email") && (
        <BulkCpnpModal
          clientId={clientId}
          clientEmail={clientEmail}
          kind={bulkOpen}
          clientProductIds={Array.from(selected)}
          items={items.filter(i => selected.has(i.id))}
          onClose={()=>setBulkOpen(null)}
        />
      )}
    </div>
  );
}

function AddClientProductModal({ clientId, existingCodes, onClose, onSaved }) {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [sortField, setSortField] = useState("name"); // "name" | "code"
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc"
  useEffect(() => {
    api.get("/products?limit=1000&light=true").then(r => setProducts(r.data.items || r.data));
  }, []);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s
      ? products.filter(p =>
          (p.name||"").toLowerCase().includes(s) ||
          (p.code||"").toLowerCase().includes(s)
        )
      : products.slice();
    // Locale-aware compare so accented/lowercase names sort correctly.
    const cmp = new Intl.Collator("it", { sensitivity: "base", numeric: true }).compare;
    base.sort((a, b) => cmp((a[sortField] || "").trim(), (b[sortField] || "").trim()));
    if (sortDir === "desc") base.reverse();
    return base;
  }, [products, q, sortField, sortDir]);
  const submit = async () => {
    if (!selected) return toast.error("Seleziona un prodotto sorgente");
    if (!code.trim()) return toast.error("Codice cliente richiesto");
    if (!name.trim()) return toast.error("Nome cliente richiesto");
    if (existingCodes.includes(code.trim()))
      return toast.error(`Codice '${code}' già usato per questo cliente`);
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/products`, {
        source_product_id: selected.id,
        code: code.trim(), name: name.trim(),
      });
      toast.success("Prodotto associato"); onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore associazione");
    } finally { setSaving(false); }
  };
  return (
    <div className="workflow-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={onClose} data-testid="cp-add-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Associa prodotto al cliente</h3>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="cp-add-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="mb-4">
          <label className="label-tiny mb-1 block">1. Seleziona il prodotto sorgente</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#707070]" strokeWidth={1.5}/>
            <input value={q} onChange={(e)=>setQ(e.target.value)}
                   placeholder="Cerca per codice o nome..."
                   className="input-luxury text-sm w-full !pl-10"
                   data-testid="cp-add-search"/>
          </div>
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="cp-add-sort-bar">
            <span className="text-[10px] tracking-luxury uppercase text-[#707070]">Ordina per:</span>
            <button type="button"
                     onClick={() => { if (sortField === "name") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortField("name"); setSortDir("asc"); } }}
                     className={`text-xs px-3 py-1 border rounded-sm transition ${sortField === "name" ? "border-[#1A1A1A] bg-[#F4F4F0] text-[#1A1A1A]" : "border-[#E5E5E0] text-[#707070] hover:border-[#C5B39B]"}`}
                     data-testid="cp-sort-name">
              Nome {sortField === "name" && (sortDir === "asc" ? "↑" : "↓")}
            </button>
            <button type="button"
                     onClick={() => { if (sortField === "code") setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortField("code"); setSortDir("asc"); } }}
                     className={`text-xs px-3 py-1 border rounded-sm transition ${sortField === "code" ? "border-[#1A1A1A] bg-[#F4F4F0] text-[#1A1A1A]" : "border-[#E5E5E0] text-[#707070] hover:border-[#C5B39B]"}`}
                     data-testid="cp-sort-code">
              Codice {sortField === "code" && (sortDir === "asc" ? "↑" : "↓")}
            </button>
          </div>
          <div className="mt-2 border border-[#E5E5E0] max-h-80 overflow-y-auto" data-testid="cp-add-list">
            {filtered.length === 0 ? (
              <div className="text-xs text-[#707070] p-4">Nessun prodotto trovato.</div>
            ) : filtered.map(p => (
              <button key={p.id} type="button"
                       onClick={()=>{ setSelected(p); if (!name) setName(p.name); if (!code) setCode(p.code); }}
                       className={`w-full text-left px-3 py-2 border-b border-[#F0F0EA] hover:bg-[#FAFAF7] flex justify-between ${selected?.id === p.id ? "bg-[#F4F4F0]" : ""}`}
                       data-testid={`cp-add-option-${p.id}`}>
                <span className="text-sm">{p.name}</span>
                <span className="text-xs text-[#707070] font-mono">{p.code}</span>
              </button>
            ))}
          </div>
          <div className="text-xs text-[#707070] mt-1" data-testid="cp-add-count">
            {q.trim() ? `${filtered.length} risultat${filtered.length === 1 ? "o" : "i"} su ${products.length}` : `${products.length} prodott${products.length === 1 ? "o" : "i"} totali`}
          </div>
        </div>
        {selected && (
          <>
            <div className="mb-4">
              <label className="label-tiny mb-1 block">2. Codice per il cliente <span className="text-[#8C3A3A]">*</span></label>
              <input value={code} onChange={(e)=>setCode(e.target.value)}
                     placeholder="es. BL-001" className="input-luxury text-sm w-full"
                     data-testid="cp-add-code"/>
            </div>
            <div className="mb-5">
              <label className="label-tiny mb-1 block">3. Nome per il cliente <span className="text-[#8C3A3A]">*</span></label>
              <input value={name} onChange={(e)=>setName(e.target.value)}
                     placeholder="es. Rossetto Beauty Lab" className="input-luxury text-sm w-full"
                     data-testid="cp-add-name"/>
            </div>
          </>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost" data-testid="cp-add-cancel">Annulla</button>
          <button onClick={submit} disabled={!selected || saving} className="btn-primary flex items-center"
                  data-testid="cp-add-submit">
            <CheckCircle2 className="w-3 h-3 mr-2" strokeWidth={2}/>{saving ? "..." : "Associa"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditClientProductModal({ clientId, item, existingCodes, onClose, onSaved }) {
  const [code, setCode] = useState(item.code);
  const [name, setName] = useState(item.name);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!code.trim()) return toast.error("Codice richiesto");
    if (!name.trim()) return toast.error("Nome richiesto");
    if (existingCodes.includes(code.trim()))
      return toast.error(`Codice '${code}' già usato per un altro prodotto di questo cliente`);
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/products/${item.id}`, {
        source_product_id: item.source_product_id,
        code: code.trim(), name: name.trim(),
      });
      toast.success("Prodotto aggiornato"); onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    } finally { setSaving(false); }
  };
  return (
    <div className="workflow-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={onClose} data-testid="cp-edit-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Modifica prodotto cliente</h3>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A]"><X className="w-4 h-4"/></button>
        </div>
        <div className="text-xs text-[#707070] mb-4">
          Sorgente: <span className="font-mono">{item.source_product_code}</span> · {item.source_product_name}
        </div>
        <div className="mb-4">
          <label className="label-tiny mb-1 block">Codice per il cliente *</label>
          <input value={code} onChange={(e)=>setCode(e.target.value)}
                 className="input-luxury text-sm w-full" data-testid="cp-edit-code"/>
        </div>
        <div className="mb-5">
          <label className="label-tiny mb-1 block">Nome per il cliente *</label>
          <input value={name} onChange={(e)=>setName(e.target.value)}
                 className="input-luxury text-sm w-full" data-testid="cp-edit-name"/>
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost">Annulla</button>
          <button onClick={submit} disabled={saving} className="btn-primary flex items-center"
                  data-testid="cp-edit-submit">
            <CheckCircle2 className="w-3 h-3 mr-2" strokeWidth={2}/>{saving ? "..." : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CpnpLangModal({ clientId, cp, onClose }) {
  const [langs, setLangs] = useState({ en: true, fr: false, es: false, de: false, pt: false });
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const selected = Object.entries(langs).filter(([, v]) => v).map(([k]) => k).join(",");
      const token = getActiveToken();
      const parts = [`t=${Date.now()}`];
      if (selected) parts.push(`langs=${encodeURIComponent(selected)}`);
      const qs = `?${parts.join("&")}`;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/products/${cp.id}/cpnp.pdf${qs}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" }
      );
      if (!res.ok) throw new Error("cpnp failed");
      const blob = await res.blob();
      const dispo = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "cpnp.pdf"; a.click();
      URL.revokeObjectURL(a.href);
      onClose();
    } catch { toast.error("Impossibile generare CPNP"); }
    finally { setBusy(false); }
  };
  return (
    <div className="workflow-ux fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={()=>!busy && onClose()} data-testid="cp-cpnp-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl">Esporta CPNP/INCI</h3>
          <button onClick={()=>!busy && onClose()} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="cp-cpnp-close"><X className="w-4 h-4"/></button>
        </div>
        <p className="text-sm text-[#707070] mb-1">
          Prodotto cliente: <strong className="text-[#1A1A1A]">{cp.code} — {cp.name}</strong>
        </p>
        <p className="text-xs text-[#707070] mb-5">
          Intestazione cliente. L'italiano è sempre incluso. Seleziona le lingue extra per le Avvertenze.
        </p>
        <div className="space-y-2 mb-6">
          {[
            { code: "en", label: "Inglese", native: "English" },
            { code: "fr", label: "Francese", native: "Français" },
            { code: "es", label: "Spagnolo", native: "Español" },
            { code: "de", label: "Tedesco", native: "Deutsch" },
            { code: "pt", label: "Portoghese", native: "Português" },
          ].map(l => (
            <label key={l.code}
                   className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition ${langs[l.code] ? "border-[#1A1A1A] bg-[#F4F4F0]" : "border-[#E5E5E0] hover:border-[#C5B39B]"}`}
                   data-testid={`cp-cpnp-lang-${l.code}`}>
              <input type="checkbox" checked={!!langs[l.code]}
                     onChange={(e)=>setLangs(s=>({...s,[l.code]:e.target.checked}))}
                     data-testid={`cp-cpnp-lang-${l.code}-check`}/>
              <span className="flex-1 text-sm">{l.label} <span className="text-[#A0A0A0]">· {l.native}</span></span>
              <span className="text-[10px] tracking-luxury uppercase text-[#A0A0A0]">{l.code}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-3">
          <button onClick={()=>onClose()} disabled={busy} className="btn-ghost" data-testid="cp-cpnp-cancel">Annulla</button>
          <button onClick={download} disabled={busy} className="btn-primary flex items-center"
                  data-testid="cp-cpnp-download">
            <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>{busy ? "Generazione..." : "Genera PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkCpnpModal({ clientId, clientEmail, kind, clientProductIds, items, onClose }) {
  // `kind` is "zip" (download CPNP only) or "email" (SMTP send with any
  // combination of CPNP/SDS attachments).
  const [langs, setLangs] = useState({ en: true, fr: false, es: false, de: false, pt: false });
  // Offer only the document types actually present in the selection.
  const hasCpnp = items.some(i => i.inci_approved);
  const hasSds = items.some(i => i.has_sds);
  const [kinds, setKinds] = useState({ cpnp: hasCpnp, sds: hasSds });
  // Pre-fill recipients with the client's own email so the user typically
  // only needs to hit "Invia email". Still editable, and the field also
  // shows autocomplete from the shared email history (localStorage).
  const [recipients, setRecipients] = useState(clientEmail || "");
  const _defaultSubject = "Documenti allegati";
  const _defaultMessage = "Buongiorno,\n\nin allegato la documentazione richiesta.\n\nCordiali saluti";
  const [subject, setSubject] = useState(_defaultSubject);
  const [message, setMessage] = useState(_defaultMessage);
  const [busy, setBusy] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyLoaded, setCompanyLoaded] = useState(false);
  useEffect(() => {
    if (kind !== "email") return;
    let active = true;
    api.get("/me/tenant").then(({ data }) => {
      if (active) setCompanyName((data.company_name || "").trim());
    }).catch(() => { if (active) toast.error("Impossibile leggere i dati societari dell'account"); })
      .finally(() => { if (active) setCompanyLoaded(true); });
    return () => { active = false; };
  }, [kind]);
  const langsCsv = () => Object.entries(langs).filter(([, v]) => v).map(([k]) => k).join(",");
  const kindsList = () => Object.entries(kinds).filter(([, v]) => v).map(([k]) => k);
  const download = async () => {
    setBusy(true);
    try {
      const token = getActiveToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/exports/cpnp.zip`,
        {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ client_product_ids: clientProductIds, langs: langsCsv() }),
        }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Errore generazione ZIP");
      }
      const blob = await res.blob();
      const dispo = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "schede-cpnp.zip";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`ZIP con ${clientProductIds.length} schede pronto`);
      onClose();
    } catch (err) { toast.error(err.message || "Errore ZIP"); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!companyName) { toast.error("Compila la Ragione sociale nel Profilo prima di inviare email"); return; }
    const to = recipients.split(/[,;\s]+/).filter(x => x.trim());
    if (!to.length) { toast.error("Inserisci almeno un destinatario"); return; }
    const chosenKinds = kindsList();
    if (!chosenKinds.length) { toast.error("Seleziona almeno un tipo di documento"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/exports/email`, {
        client_product_ids: clientProductIds,
        langs: langsCsv(),
        kinds: chosenKinds,
        recipients: to,
        subject: subject.trim(),
        message: message.trim(),
      });
      toast.success(`Email inviata a ${r.data.recipients} destinatari (${r.data.exported} allegat${r.data.exported === 1 ? "o" : "i"})`);
      addEmails(to);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio email");
    } finally { setBusy(false); }
  };
  const title = kind === "email" ? "Invia documenti via email" : "Scarica ZIP schede CPNP/INCI";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={()=>!busy && onClose()} data-testid="cp-bulk-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-2xl">{title}</h3>
          <button onClick={()=>!busy && onClose()} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="cp-bulk-close"><X className="w-4 h-4"/></button>
        </div>
        <p className="text-xs text-[#707070] mb-4">
          {clientProductIds.length} prodotto/i selezionato/i · Intestazione cliente applicata a ogni PDF.
        </p>
        <div className="mb-5 max-h-32 overflow-y-auto text-xs text-[#4A4A4A] border border-[#F0F0EA] rounded-sm p-2 bg-[#FAFAF7]">
          {items.map(cp => (
            <div key={cp.id} className="py-0.5">· <span className="font-mono">{cp.code}</span> — {cp.name}</div>
          ))}
        </div>
        {kind === "email" && (
          <div className="mb-5">
            <div className="label-tiny mb-2">Tipi di documento da allegare</div>
            <div className="flex flex-col gap-2">
              <label className={`flex flex-wrap items-center gap-3 p-3 border rounded-sm transition ${!hasCpnp ? "cursor-not-allowed border-[#E5E5E0]" : kinds.cpnp ? "border-[#1A1A1A] bg-[#F4F4F0] cursor-pointer" : "border-[#E5E5E0] hover:border-[#C5B39B] cursor-pointer"}`}
                     data-testid="cp-bulk-kind-cpnp">
                <input type="checkbox" checked={!!kinds.cpnp} disabled={!hasCpnp}
                       onChange={(e)=>setKinds(k=>({...k, cpnp: e.target.checked}))}
                       data-testid="cp-bulk-kind-cpnp-checkbox"/>
                <span className="flex-1 text-sm">Schede CPNP/INCI</span>
                {hasCpnp ? <span className="text-[10px] tracking-luxury uppercase text-[#707070]" data-testid="cp-bulk-kind-cpnp-hint">Avvertenze multilingua</span>
                  : <ProductStatus tone="warning" testId="cp-bulk-kind-cpnp-status">Non disponibile</ProductStatus>}
              </label>
              <label className={`flex flex-wrap items-center gap-3 p-3 border rounded-sm transition ${!hasSds ? "cursor-not-allowed border-[#E5E5E0]" : kinds.sds ? "border-[#1A1A1A] bg-[#F4F4F0] cursor-pointer" : "border-[#E5E5E0] hover:border-[#C5B39B] cursor-pointer"}`}
                     data-testid="cp-bulk-kind-sds">
                <input type="checkbox" checked={!!kinds.sds} disabled={!hasSds}
                       onChange={(e)=>setKinds(k=>({...k, sds: e.target.checked}))}
                       data-testid="cp-bulk-kind-sds-checkbox"/>
                <span className="flex-1 text-sm">Schede di Sicurezza</span>
                {hasSds ? <span className="text-[10px] tracking-luxury uppercase text-[#707070]" data-testid="cp-bulk-kind-sds-hint">PDF fornitore</span>
                  : <ProductStatus tone="warning" testId="cp-bulk-kind-sds-status">Non disponibile</ProductStatus>}
              </label>
            </div>
          </div>
        )}
        {kind === "email" && <ClientEmailAvailability items={items} kinds={kinds} />}
        {(kind === "zip" || kinds.cpnp) && (
        <div className="mb-5">
          <div className="label-tiny mb-2">Lingue CPNP extra (italiano sempre incluso)</div>
          <div className="flex gap-2 flex-wrap">
            {[
              { code: "en", label: "EN" }, { code: "fr", label: "FR" },
              { code: "es", label: "ES" }, { code: "de", label: "DE" }, { code: "pt", label: "PT" },
            ].map(l => (
              <label key={l.code}
                     className={`px-3 py-1.5 border text-xs cursor-pointer rounded-sm ${langs[l.code] ? "border-[#1A1A1A] bg-[#F4F4F0]" : "border-[#E5E5E0] hover:border-[#C5B39B]"}`}
                     data-testid={`cp-bulk-lang-${l.code}`}>
                <input type="checkbox" className="mr-1.5" checked={!!langs[l.code]}
                       onChange={(e)=>setLangs(s=>({...s,[l.code]:e.target.checked}))}/>
                {l.label}
              </label>
            ))}
          </div>
        </div>
        )}
        {kind === "email" && (
          <>
            <div className="mb-3">
              <div className="label-tiny mb-1">Destinatari (separati da virgola)</div>
              <RecipientsField
                value={recipients}
                onChange={setRecipients}
                placeholder="mario@example.com, laboratorio@lab.it"
                testIdPrefix="cp-bulk-email"
              />
            </div>
            <div className="mb-3">
              <div className="label-tiny mb-1">Oggetto</div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="text-sm font-medium break-words text-[#1A1A1A]"
                      data-testid="cp-bulk-email-company-prefix">
                  {companyName ? `${companyName} /` : companyLoaded ? "Ragione sociale mancante" : "Caricamento..."}
                </span>
                <input value={subject} onChange={(e)=>setSubject(e.target.value)}
                       placeholder="Documenti allegati"
                       className="input-luxury text-sm w-full min-w-0 sm:flex-1"
                       data-testid="cp-bulk-email-subject"/>
              </div>
              {companyLoaded && !companyName && <p className="text-xs text-[#8C3A3A] mt-2" data-testid="cp-bulk-email-company-error">Compila la Ragione sociale nel Profilo prima dell'invio.</p>}
            </div>
            <div className="mb-5">
              <div className="label-tiny mb-1">Messaggio (opzionale)</div>
              <textarea value={message} onChange={(e)=>setMessage(e.target.value)}
                     placeholder="Buongiorno, in allegato le schede aggiornate..."
                     rows={4} className="input-luxury text-sm w-full"
                     data-testid="cp-bulk-email-message"/>
              <p className="text-xs text-[#707070] mt-2" data-testid="cp-bulk-email-signature-note">Dopo i saluti vengono aggiunti Ragione sociale e dati societari del tuo account, senza loghi del brand.</p>
            </div>
          </>
        )}
        <div className="flex justify-end gap-3">
          <button onClick={()=>onClose()} disabled={busy} className="btn-ghost" data-testid="cp-bulk-cancel">Annulla</button>
          {kind === "zip" ? (
            <button onClick={download} disabled={busy} className="btn-primary flex items-center"
                    data-testid="cp-bulk-zip-download">
              <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>{busy ? "Generazione..." : "Genera ZIP"}
            </button>
          ) : (
            <button onClick={send} disabled={busy || !recipients.trim() || !companyLoaded || !companyName} className="btn-primary flex items-center"
                    data-testid="cp-bulk-email-send">
              <Mail className="w-3 h-3 mr-2" strokeWidth={1.5}/>{busy ? "Invio in corso..." : "Invia email"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function SdsLangModal({ clientId, cp, onClose }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const token = getActiveToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/products/${cp.id}/sds.pdf?t=${Date.now()}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "sds failed");
      }
      const blob = await res.blob();
      const dispo = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `${cp.code}_SDS.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
      onClose();
    } catch (e) {
      toast.error(e.message || "Impossibile scaricare la scheda di sicurezza");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={()=>!busy && onClose()} data-testid="cp-sds-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl">Scheda di Sicurezza</h3>
          <button onClick={()=>!busy && onClose()} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="cp-sds-close"><X className="w-4 h-4"/></button>
        </div>
        <p className="text-sm text-[#707070] mb-1">
          Prodotto cliente: <strong className="text-[#1A1A1A]">{cp.code} — {cp.name}</strong>
        </p>
        <p className="text-xs text-[#707070] mb-6">
          Verrà scaricata la scheda di sicurezza (PDF) caricata sul prodotto sorgente.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={()=>onClose()} disabled={busy} className="btn-ghost" data-testid="cp-sds-cancel">Annulla</button>
          <button onClick={download} disabled={busy} className="btn-primary flex items-center"
                  data-testid="cp-sds-download">
            <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>{busy ? "Download..." : "Scarica PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkSdsModal({ clientId, clientProductIds, items, onClose }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const token = getActiveToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/exports/sds.zip`,
        { method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ client_product_ids: clientProductIds }) }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "bulk sds failed");
      }
      const blob = await res.blob();
      const dispo = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(dispo);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : "schede-sicurezza.zip";
      a.click();
      URL.revokeObjectURL(a.href);
      onClose();
    } catch (e) {
      toast.error(e.message || "Errore export SDS");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
         onClick={()=>!busy && onClose()} data-testid="cp-bulk-sds-modal">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-serif text-2xl">ZIP Schede di Sicurezza</h3>
          <button onClick={()=>!busy && onClose()} className="text-[#707070] hover:text-[#1A1A1A]"
                  data-testid="cp-bulk-sds-close"><X className="w-4 h-4"/></button>
        </div>
        <p className="text-sm text-[#707070] mb-1">
          {items.length} prodott{items.length === 1 ? "o selezionato" : "i selezionati"}
        </p>
        <p className="text-xs text-[#707070] mb-6">
          Lo ZIP conterrà la scheda di sicurezza (PDF fornitore) di ogni prodotto
          sorgente selezionato. I prodotti senza scheda caricata vengono elencati
          nel report.
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={()=>onClose()} disabled={busy} className="btn-ghost" data-testid="cp-bulk-sds-cancel">Annulla</button>
          <button onClick={download} disabled={busy} className="btn-primary flex items-center"
                  data-testid="cp-bulk-sds-confirm">
            <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>{busy ? "Generazione..." : "Genera ZIP"}
          </button>
        </div>
      </div>
    </div>
  );
}
